import { useRef, useState } from 'react';
import type { ComponentType } from 'react';
import { Formik } from 'formik';
import { useShowToast } from '@/hooks';
import { useEditScoreCardMutation } from '@/store/query/interviews';
import { Box as _Box, Flex as _Flex } from '@chakra-ui/react';
import type { BoxProps } from '@chakra-ui/react';
import {
  ScorecardFillBody,
  mapScorecardValuesToPayload,
  useScorecardValidation,
} from '@/components/shared/scorecard';
import { useScorecardFormHelper } from './useScorecardFormHelper';
import { OverallEvaluationBanner } from './OverallEvaluationBanner';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useScorecardAutoSaveHelper } from '@/components/ats/collect-score-card/useScorecardAutoSaveHelper';
import { ScorecardAutoSave } from '@/components/ats/collect-score-card/AutoSaveScorecard';
import { useScorecardFillWithAI } from './useScorecardFillWithAI';
import { useGetInterviewTranscriptQuery } from '@/store/query/interview';
import {
  AIFillScorecardLoader,
  FillWithAIHeader,
  ScorecardFooter,
} from './components';
import type { InterviewType, ScorecardType } from '../types/interview';
import './interviewScorecard.css';

// Chakra 2.10 + TypeScript 4.9 trip TS2590 ("union type too complex") when
// style-prop-heavy components are composed in a single JSX tree. Collapsing
// the components to React.ComponentType<any> kills the inference cascade.
// Runtime behavior is unchanged; Chakra style-prop IntelliSense is suppressed
// in this file only.
type CC = ComponentType<any>;
const Box = _Box as CC;
const Flex = _Flex as CC;

export const InterviewScorecardForm = ({
  scorecard,
  interview,
  onSuccess = () => {},
  formContainerStyles = {},
  scorecardFooterStyles = {},
}: {
  scorecard: ScorecardType;
  interview: InterviewType;
  onSuccess: () => void;
  formContainerStyles?: BoxProps;
  scorecardFooterStyles?: BoxProps;
}) => {
  const { can_edit } = scorecard;

  const updateAttachmentChanges = (attachmentDetails) => {
    const type = attachmentDetails.type;
    const attachments = attachmentDetails.attachments;
    const attachmentKey = attachmentDetails.attachmentKey;
    const attachmentId = attachmentDetails.attachmentId;

    // eslint-disable-next-line react-hooks/immutability -- setInitialValues is assigned after useState below
    setInitialValues((prev: any) => {
      const newValues = { ...formRef?.current?.values };

      if (type === 'add' && attachments && attachmentKey) {
        newValues[attachmentKey] = [
          ...(prev[attachmentKey] || []),
          ...attachments,
        ];
      } else if (type === 'remove' && attachmentKey && attachmentId) {
        newValues[attachmentKey] = (prev[attachmentKey] || []).filter(
          (attachment) => attachment.id !== attachmentId
        );
      } else if (type === 'updateError' && attachments && attachmentKey) {
        // Mark the failed files with hasError flag
        const currentAttachments = prev[attachmentKey] || [];
        newValues[attachmentKey] = currentAttachments.map((file) => {
          // Check if this file is in the failed files list (match by name and size)
          const isFailed = attachments.some(
            (failedFile) =>
              failedFile.name === file.name &&
              failedFile.size === file.size &&
              file.hasError
          );
          return isFailed ? { ...file, hasError: true } : file;
        });
      }

      return newValues;
    });
  };

  const { getScoreCardForm, getInitialValues } = useScorecardFormHelper(
    updateAttachmentChanges
  );

  const currUser = useSelector(selectCurrUser);
  const isSomeOtherUserHasDraftScorecard =
    scorecard?.status === 'draft' &&
    scorecard?.submitted_by?.id &&
    scorecard?.submitted_by?.id !== currUser?.id;

  const { data: transcriptData } = useGetInterviewTranscriptQuery(
    { interviewId: interview?.id },
    { skip: !interview?.id || interview?.transcript_status !== 'completed' }
  );
  const [updateScorecard, { isLoading: isUpdating }] =
    useEditScoreCardMutation();

  const [initialValues, setInitialValues] = useState(
    getInitialValues({
      scorecard,
      isSubmitted: isSomeOtherUserHasDraftScorecard ? false : true,
    })
  );
  // The FormBuilder-style config supplies the rich-editor field props
  // (attachment handlers) for the open_question / feedback slots and tells the
  // validator which sections are shown. Questions don't change, so it's built
  // once.
  const [formData] = useState(() =>
    getScoreCardForm({ scorecard, isEdit: can_edit })
  );
  const { errors, scrollToErrorToken, validate } = useScorecardValidation(
    scorecard,
    formData
  );
  const [isAiPolling, setIsAiPolling] = useState(false);
  const [description, setDescription] = useState<string>('');
  const [formKey, setFormKey] = useState(0);
  // AI fill never sets the overall evaluation; the banner nudges the reviewer to
  // fill it. Shown above the rating via the builder's renderRating slot.
  const [showAiBanner, setShowAiBanner] = useState(false);
  const { handleAutoSave, isSaved } = useScorecardAutoSaveHelper({
    atsApplicationId: scorecard?.ats_application_id,
    scorecardId: scorecard?.id,
    questions: scorecard?.questions,
  });

  const formRef = useRef(null);
  const draftValuesRef = useRef<Record<string, any>>({});
  const toast = useShowToast();
  const { id, ats_application_id } = scorecard || {};

  const { handleFillWithAi, isFilling, isPolling } = useScorecardFillWithAI({
    ats_application_id,
    scorecardId: id,
    interviewId: interview?.id,
    description: description,
    onSuccess: (newScorecard, isError) => {
      // draftValuesRef is populated synchronously in handleFillWithAiAndCaptureDraft before isFilling flips
      if (!isError && newScorecard) {
        const draftValues = draftValuesRef.current;

        const newValues = getInitialValues({
          scorecard: newScorecard,
          isSubmitted: true,
        });

        const draftFeedback = String(draftValues.feedback || '').trim();
        const aiFeedback = String(newValues.feedback || '').trim();
        newValues.feedback = [draftFeedback, aiFeedback]
          .filter(Boolean)
          .join('<p dir="auto"></p>');

        scorecard?.questions?.forEach((question, idx) => {
          const key = idx + 1;
          const draftAnswer = String(draftValues[key] || '').trim();
          const aiAnswer = String(newValues[key] || '').trim();
          if (question.type === 'open_question') {
            newValues[key] = [draftAnswer, aiAnswer]
              .filter(Boolean)
              .join('<p dir="auto"></p>');
          }
        });

        setInitialValues(newValues);
        setShowAiBanner(true);
        // Remount so the rich-text editors re-seed from the merged content
        // (tiptap only reads its field value on mount).
        setFormKey((prev) => prev + 1);
      }
      setIsAiPolling(false);
    },
    onStart: () => {
      setIsAiPolling(true);
    },
  });

  const handleFillWithAiAndCaptureDraft = (params: { description: string }) => {
    draftValuesRef.current = formRef.current?.values || {};
    return handleFillWithAi(params);
  };

  const onClear = () => {
    // Clear all answers, feedback and rating, then remount so the editors reset.
    const clearedValues: Record<string, any> = {};
    if (scorecard?.questions) {
      scorecard.questions.forEach((_, idx) => {
        clearedValues[idx + 1] = '';
      });
    }
    clearedValues.feedback = '';
    clearedValues.rating = undefined;
    setInitialValues(clearedValues);
    setShowAiBanner(false);
    setFormKey((prev) => prev + 1);
    formRef.current?.resetForm();
  };

  const handleFormSubmit = (rawValues) => {
    // Gate on the BE-matching check (required + per-answer validity); the
    // builder surfaces inline errors + scrolls when this blocks. Submit the
    // coerced values (yes_no lowercased, numeric stars to ints).
    const { valid, values } = validate(rawValues);
    if (!valid) return;
    const payload = mapScorecardValuesToPayload(values, scorecard, 'submitted');

    updateScorecard({
      scorecardId: id,
      payload,
      applicantId: ats_application_id,
    }).then((response) => {
      if ('error' in response) {
        const message = response?.error?.[0];
        toast({
          title: `Unable to submit scorecard`,
          description: message,
          status: 'error',
        });
      } else {
        toast({ title: `Scorecard submitted successfully` });
        onSuccess();
      }
    });
  };

  const isEmptyTranscript = transcriptData?.transcript?.length === 0;

  const renderContent = () => {
    if (isFilling || isPolling || isAiPolling) {
      return <AIFillScorecardLoader />;
    }

    return (
      <Formik
        key={formKey}
        enableReinitialize
        innerRef={formRef}
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
      >
        <>
          <ScorecardFillBody
            scorecard={scorecard}
            formConfig={formData}
            ratingBanner={showAiBanner ? <OverallEvaluationBanner /> : null}
            errors={errors}
            scrollToErrorToken={scrollToErrorToken}
          />
          {scorecard?.status !== 'submitted' && (
            <ScorecardAutoSave onAutoSave={handleAutoSave} />
          )}
        </>
      </Formik>
    );
  };

  return (
    <Flex flexDir="column" w="100%" h="100%" overflow="hidden">
      {interview?.transcript_status === 'completed' && (
        <FillWithAIHeader
          description={description}
          isEmptyTranscript={isEmptyTranscript}
          isLoading={isFilling || isPolling || isAiPolling}
          handleFillWithAi={handleFillWithAiAndCaptureDraft}
          setDescription={setDescription}
        />
      )}

      <Box flex="1" overflowY="auto" p="28px" {...formContainerStyles}>
        {renderContent()}
      </Box>

      {can_edit && !isFilling && !isPolling && !isAiPolling && (
        <ScorecardFooter
          isSaved={isSaved}
          isUpdating={isUpdating}
          formRef={formRef}
          scorecard={scorecard}
          onClear={onClear}
          scorecardFooterStyles={scorecardFooterStyles}
        />
      )}
    </Flex>
  );
};
